import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, fmt } from '../api'
import { useSession } from '@argo/common/session'
import { BankDesk, BankPanels } from '../components/Bank'

/**
 * Money in and out. With the TDB bridge on, a customer deposits and withdraws
 * through the bank (BankPanels) and operators decide at the BankDesk. The
 * direct form — deposit mints, withdraw burns, no money moves anywhere — is
 * then for operators and issuers only: offered to a customer, its withdraw
 * would destroy their balance and pay them nothing.
 */
export default function Ramp() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { account, me } = useSession()
  const canDeposit = (me?.roles ?? []).some(r => r === 'operator' || r === 'issuer')
  const operator = (me?.roles ?? []).includes('operator')
  const needsPin = me?.pin.set ?? false
  const bank = useQuery({ queryKey: ['bank', account], queryFn: () => api.bank(account), enabled: !!account })
  const bankOn = bank.data?.enabled ?? false
  const direct = !bankOn || canDeposit

  const [kind, setKind] = useState<'deposit' | 'withdraw'>(canDeposit ? 'deposit' : 'withdraw')
  const [symbol, setSymbol] = useState('AUD')
  const [amount, setAmount] = useState('')
  const [pin, setPin] = useState('')
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)

  const history = useQuery({ queryKey: ['ramp', account], queryFn: () => api.ramp(account), enabled: !!account, refetchInterval: 10_000 })
  const balances = useQuery({ queryKey: ['balances', account], queryFn: () => api.balances(account), enabled: !!account })
  const held = Number(balances.data?.data.find(b => b.symbol === symbol)?.amount ?? 0)

  const run = useMutation({
    mutationFn: () => {
      const body = { account, symbol, amount: Number(amount), pin: pin || undefined }
      return kind === 'deposit' ? api.deposit(body) : api.withdraw(body)
    },
    onSuccess: () => {
      setNote({ text: t('done'), kind: 'ok' })
      setAmount('')
      qc.invalidateQueries({ queryKey: ['ramp', account] })
      qc.invalidateQueries({ queryKey: ['balances', account] })
    },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setNote(null)
    if (!account) { setNote({ text: t('enterAccount'), kind: 'err' }); return }
    if (!(Number(amount) > 0)) { setNote({ text: t('enterAmount'), kind: 'err' }); return }
    if (kind === 'withdraw' && Number(amount) > held) { setNote({ text: t('insufficient'), kind: 'err' }); return }
    run.mutate()
  }

  return (
    <div className="page page-mid cols cols-side">
      <div className="side">
      <BankPanels />
      {direct && (
      <div className="panel">
        <div className="panel-head">
          <span className="grow">{t('ramp')}</span>
          {account && <span className="chip accent">{account}</span>}
        </div>
        <div className="panel-body">
          <form onSubmit={submit}>
            <div className="side-toggle">
              <button type="button" className={kind === 'deposit' ? 'on-buy' : ''}
                disabled={!canDeposit} onClick={() => setKind('deposit')}
                title={canDeposit ? undefined : t('errCredentials')}>
                {t('deposit')}
              </button>
              <button type="button" className={kind === 'withdraw' ? 'on-sell' : ''} onClick={() => setKind('withdraw')}>
                {t('withdraw')}
              </button>
            </div>

            <div className="field">
              <label htmlFor="rsym">{t('symbol')}</label>
              <div className="input-wrap">
                <input id="rsym" value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="ramt">
                {t('amount')}
                <b>{t('available')} {fmt(held)} {symbol}</b>
              </label>
              <div className="input-wrap">
                <input id="ramt" inputMode="numeric" value={amount} placeholder="0"
                  onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
                <span className="unit">{symbol}</span>
              </div>
            </div>

            {needsPin && (
              <div className="field">
                <label htmlFor="rpin">{t('pin')}</label>
                <div className="input-wrap">
                  <input id="rpin" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
                    value={pin} onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))} />
                </div>
              </div>
            )}

            <button className={`submit ${kind === 'deposit' ? 'buy' : 'sell'}`} type="submit" disabled={run.isPending || !account}>
              {run.isPending ? t('loading') : t(kind)}
            </button>
            {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}
          </form>
        </div>
      </div>
      )}

      </div>

      <div style={{ minWidth: 0 }}>
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('deposits')}</span></div>
        <div className="panel-body flush">
          {(history.data?.data.length ?? 0) === 0 ? (
            <div className="empty">{t('noData')}</div>
          ) : history.data?.data.map((r, i) => (
            <div className="list-row" key={`${r.chain_txid}-${i}`}>
              <span className={`chip ${r.kind === 'deposit' ? 'up' : 'down'}`}>{t(r.kind)}</span>
              <div className="grow">
                <div className="title num">{fmt(r.amount)} {r.symbol}</div>
                <div className="sub">{new Date(r.created_at).toLocaleString()} · {r.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {operator && <BankDesk />}
      </div>
    </div>
  )
}
